import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getCookieConsent } from './CookieConsent';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

const GoogleAnalytics = () => {
  const location = useLocation();
  const [hasConsent, setHasConsent] = useState<boolean | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Check consent on mount and listen for changes
  useEffect(() => {
    const checkConsent = () => {
      const consent = getCookieConsent();
      setHasConsent(consent === "accepted");
    };

    checkConsent();

    // Listen for consent changes
    const handleConsentChange = (event: CustomEvent) => {
      setHasConsent(event.detail === "accepted");
    };

    window.addEventListener("cookieConsentChanged", handleConsentChange as EventListener);
    return () => {
      window.removeEventListener("cookieConsentChanged", handleConsentChange as EventListener);
    };
  }, []);

  // Initialize GA4 script ONLY if consent is given
  useEffect(() => {
    if (!GA_MEASUREMENT_ID) {
      console.log('Google Analytics: No measurement ID configured');
      return;
    }

    // Don't load if no consent or already loaded
    if (!hasConsent || isLoaded) {
      return;
    }

    // Check if script already exists
    if (document.querySelector(`script[src*="googletagmanager.com/gtag"]`)) {
      setIsLoaded(true);
      return;
    }

    // Load GA only after consent
    const loadGA = () => {
      // Add gtag.js script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      document.head.appendChild(script);

      // Initialize dataLayer and gtag
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('config', GA_MEASUREMENT_ID, {
        send_page_view: false // We'll handle page views manually for SPA
      });

      setIsLoaded(true);
      console.log('Google Analytics: Loaded after user consent');
    };

    // Small delay for better performance
    const timeout = setTimeout(loadGA, 100);
    return () => clearTimeout(timeout);
  }, [hasConsent, isLoaded]);

  // Track page views on route change (only if loaded)
  useEffect(() => {
    if (!GA_MEASUREMENT_ID || !window.gtag || !isLoaded) return;

    window.gtag('event', 'page_view', {
      page_path: location.pathname + location.search,
      page_title: document.title
    });
  }, [location, isLoaded]);

  return null;
};

export default GoogleAnalytics;
